const MAX_DIMENSION = 1100;
const INITIAL_QUALITY = 0.8;
const MIN_QUALITY = 0.3;
// Sem Firebase Storage (Spark não deixa habilitar sem virar Blaze — decisão do
// usuário), a imagem vira data URL DENTRO do próprio doc do post no Firestore,
// junto com capa + as outras imagens inline + o texto — tudo isso precisa caber
// no limite de ~1MiB (1.048.487 bytes) de 1 documento só. 150KB por imagem dá
// margem pra uns 5-6 imagens num post sem chegar perto do teto.
const TARGET_MAX_BYTES = 150_000;

// Avatar do autor: quadrado pequeno, fica salvo 1x em `authors/{uid}` e
// denormalizado em CADA post criado (ver api/blog/posts/index.ts) — precisa
// ser bem mais leve que uma imagem de post pra não pesar em toda listagem.
const AVATAR_MAX_DIMENSION = 240;
const AVATAR_TARGET_MAX_BYTES = 30_000;

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error('Falha ao ler arquivo de imagem'));
    reader.readAsDataURL(file);
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Falha ao carregar imagem'));
    img.src = src;
  });
}

function scaledDimensions(width: number, height: number, maxDimension: number) {
  if (width <= maxDimension && height <= maxDimension) return { width, height };
  const scale = maxDimension / Math.max(width, height);
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

function dataUrlByteLength(dataUrl: string): number {
  const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
  return Math.ceil((base64.length * 3) / 4);
}

async function compressToDataUrl(file: File, maxDimension: number, targetMaxBytes: number): Promise<string> {
  const original = await readFileAsDataUrl(file);
  const img = await loadImage(original).catch(() => null);
  if (!img) return original;

  const { width, height } = scaledDimensions(img.width, img.height, maxDimension);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return original;

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);

  let quality = INITIAL_QUALITY;
  let output = canvas.toDataURL('image/jpeg', quality);
  while (dataUrlByteLength(output) > targetMaxBytes && quality > MIN_QUALITY) {
    quality -= 0.1;
    output = canvas.toDataURL('image/jpeg', quality);
  }
  return output;
}

/** Redimensiona e comprime pra uma data URL JPEG pequena o bastante pra várias
 *  imagens caberem no mesmo doc do post (mesma técnica de
 *  `dashboard/utils/imageResize.ts`, com teto bem mais apertado — ali é 1 foto
 *  de capa sozinha no documento do evento, aqui pode ter capa + várias imagens
 *  inline no mesmo post). Se nem na qualidade mínima couber, devolve o melhor
 *  resultado obtido mesmo assim — quem chama decide se avisa o usuário. */
export function compressImageToDataUrl(file: File): Promise<string> {
  return compressToDataUrl(file, MAX_DIMENSION, TARGET_MAX_BYTES);
}

/** Mesma técnica, teto bem mais apertado — o avatar do autor é denormalizado
 *  em CADA post que essa pessoa cria (ver api/blog/posts/index.ts), então
 *  precisa ficar pequeno pra não pesar a listagem pública do blog. */
export function compressAvatarToDataUrl(file: File): Promise<string> {
  return compressToDataUrl(file, AVATAR_MAX_DIMENSION, AVATAR_TARGET_MAX_BYTES);
}
